import { z } from 'zod'

// Debug flag to control success message logging
const ENABLE_SUCCESS_LOGGING = true

/**
 * Enhanced Zod Validation Utility
 *
 * This utility provides improved debugging for Zod schema validation failures.
 * It shows both expected and received data structures to make debugging easier.
 *
 * Key Features:
 * - Clear "expected vs received" structure comparison
 * - One-liner validation functions
 * - Detailed error logging for development
 * - Type-safe validation for API requests/responses
 * - Optional success logging (controlled by ENABLE_SUCCESS_LOGGING)
 */

type ValidationResult<T> =
  | {
      success: true
      data: T
    }
  | {
      success: false
      error: string
      expectedStructure: string
      receivedStructure: string
    }

/**
 * Enhanced Zod validation with detailed debugging information
 *
 * @param schema - The Zod schema to validate against
 * @param data - The data to validate
 * @param schemaName - Optional name for the schema (used in error messages)
 * @returns ValidationResult with either success + data or detailed error info
 */
function validateWithDebugInfo<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  schemaName?: string,
): ValidationResult<T> {
  const result = schema.safeParse(data)

  if (result.success) {
    return {
      success: true,
      data: result.data,
    }
  }

  // Generate expected structure from schema
  const expectedStructure = generateExpectedStructure(schema)

  // Generate received structure
  const receivedStructure = generateReceivedStructure(data)

  // Create detailed error message
  const errorMessage = `
=== ZOD VALIDATION FAILED ${schemaName ? `for ${schemaName}` : ''} ===

Expected structure:
${expectedStructure}

Received structure:
${receivedStructure}

Validation errors:
${result.error.issues
  .map((issue) => {
    const extra =
      'expected' in issue && 'received' in issue
        ? ` (expected: ${issue.expected}, received: ${issue.received})`
        : ''
    return `  • Path: ${issue.path.join('.')} - ${issue.message}${extra}`
  })
  .join('\n')}
`

  return {
    success: false,
    error: errorMessage,
    expectedStructure,
    receivedStructure,
  }
}

/**
 * One-liner validation with enhanced debugging for API responses
 *
 * This is the primary function for validating API responses in components.
 * It automatically logs detailed debugging information on validation failure.
 * Returns null for undefined data (common during React Query loading states).
 *
 * @param schema - The Zod schema to validate against
 * @param data - The API response data to validate (returns null if undefined)
 * @param componentName - Name of the component (for debugging context)
 * @returns Validated data or null if validation fails or data is undefined
 *
 * @example
 * ```tsx
 * const validatedData = validateAndLog(learningsRetrieveResponse, apiData, 'AcademyPage');
 * ```
 */
export function validateAndLog<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  componentName?: string,
): T | null {
  // Don't validate if data is undefined (common during loading states)
  if (data === undefined) {
    return null
  }

  const result = validateWithDebugInfo(schema, data, componentName)

  if (result.success) {
    if (ENABLE_SUCCESS_LOGGING) {
      console.log(
        `✅ Zod validation passed${componentName ? ` for ${componentName}` : ''}`,
      )
    }
    return result.data
  }

  console.error(`${componentName ? `[${componentName}] ` : ''}${result.error}`)
  return null
}

/**
 * Validation for data BEFORE sending to server (throws on failure)
 *
 * Use this when you want to prevent invalid data from being sent to the API.
 * It throws an error if validation fails, which can be caught by mutation error handlers.
 *
 * @param schema - The Zod schema to validate against
 * @param data - The request data to validate
 * @param operationName - Name of the operation (for debugging context)
 * @returns Validated data (guaranteed to be valid)
 * @throws Error if validation fails
 *
 * @example
 * ```tsx
 * try {
 *   const validData = validateForRequest(updateSchema, requestData, 'SaveUser');
 *   updateMutation.mutate(validData);
 * } catch (error) {
 *   // Show user-friendly error message
 * }
 * ```
 */
export function validateForRequest<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  operationName?: string,
): T {
  const result = validateWithDebugInfo(schema, data, operationName)

  if (result.success) {
    if (ENABLE_SUCCESS_LOGGING) {
      console.log(
        `✅ Zod validation passed${operationName ? ` for ${operationName}` : ''}`,
      )
    }
    return result.data
  }

  // Log the detailed error for developers
  console.error(`${operationName ? `[${operationName}] ` : ''}${result.error}`)

  // Throw a user-friendly error that can be caught by mutation error handlers
  throw new Error(
    `Invalid data for ${operationName || 'request'}. Check console for details.`,
  )
}

// ============================================================================
// INTERNAL HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a readable structure representation from Zod schema
 * @internal
 */
function generateExpectedStructure(schema: z.ZodSchema): string {
  try {
    // Create a sample object that would pass validation
    const sampleData = generateSampleFromSchema(schema)
    return JSON.stringify(sampleData, null, 2)
  } catch {
    return 'Unable to generate expected structure from schema'
  }
}

/**
 * Generate a readable structure representation from received data
 * @internal
 */
function generateReceivedStructure(data: unknown): string {
  if (data === null) return 'null'
  if (data === undefined) return 'undefined'

  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return `[${typeof data}] ${String(data)}`
  }
}

/**
 * Attempt to generate sample data from a Zod schema
 * @internal
 */
function generateSampleFromSchema(schema: z.ZodSchema): any {
  const def = (schema as any)._def

  if (schema instanceof z.ZodObject) {
    const shape = def.shape()
    const sample: any = {}

    for (const [key, fieldSchema] of Object.entries(shape)) {
      try {
        sample[key] = generateSampleFromSchema(fieldSchema as z.ZodSchema)
      } catch {
        sample[key] = `<${getZodTypeName(fieldSchema as z.ZodSchema)}>`
      }
    }

    return sample
  }

  if (schema instanceof z.ZodArray) {
    return [generateSampleFromSchema(def.type)]
  }

  if (schema instanceof z.ZodOptional) {
    return generateSampleFromSchema(def.innerType)
  }

  if (schema instanceof z.ZodNullable) {
    return generateSampleFromSchema(def.innerType)
  }

  // Return type placeholder for primitive types
  return `<${getZodTypeName(schema)}>`
}

/**
 * Get readable type name from Zod schema
 * @internal
 */
function getZodTypeName(schema: z.ZodSchema): string {
  const typeName = (schema as any)._def.typeName

  switch (typeName) {
    case 'ZodString':
      return 'string'
    case 'ZodNumber':
      return 'number'
    case 'ZodBoolean':
      return 'boolean'
    case 'ZodArray':
      return 'array'
    case 'ZodObject':
      return 'object'
    case 'ZodOptional':
      return 'optional'
    case 'ZodNullable':
      return 'nullable'
    default:
      return typeName || 'unknown'
  }
}
